/**
 * Asian mangrove species (true mangroves + common mangrove associates).
 * Sources: IUCN Mangrove Specialist Group, FAO mangrove guide for Asia,
 *          Spalding et al. "World Atlas of Mangroves" (2010).
 *
 * Each entry: { scientific, common, family, local?, regions: string[] }
 * `regions` tags let projects pre-filter the dropdown to species likely
 * to occur at that site.
 *
 * Region codes:
 *   sundarbans       — Bangladesh + India Sundarbans delta
 *   south_asia       — India / Sri Lanka / Pakistan coasts (outside Sundarbans)
 *   southeast_asia   — Indonesia / Malaysia / Thailand / Vietnam / Brunei / Singapore
 *   philippines      — Philippine archipelago
 *   east_asia        — South China / Taiwan / Hong Kong / Japan (Ryukyu)
 *   indo_pacific     — Broad Indo-Pacific distribution
 */
export const REGIONS = [
  { code: 'southeast_asia', label: 'Southeast Asia (ID/MY/TH/VN/SG) — default' },
  { code: 'philippines', label: 'Philippines' },
  { code: 'sundarbans', label: 'Sundarbans (BD/IN delta)' },
  { code: 'south_asia', label: 'South Asia (IN/LK/PK coast)' },
  { code: 'east_asia', label: 'East Asia (CN/TW/HK/JP)' },
  { code: 'indo_pacific', label: 'Indo-Pacific (broad)' },
  { code: 'all', label: 'All Asian species (no filter)' },
];

export const ASIAN_MANGROVES = [
  // ---- Rhizophoraceae ----
  { scientific: 'Rhizophora apiculata', common: 'Tall-stilt mangrove', family: 'Rhizophoraceae', local: 'Bakau minyak / Bakhau',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Rhizophora mucronata', common: 'Asiatic mangrove / Red mangrove', family: 'Rhizophoraceae', local: 'Bakau kurap',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Rhizophora stylosa', common: 'Spotted mangrove', family: 'Rhizophoraceae', local: 'Bakau pasir',
    regions: ['southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Rhizophora x lamarckii', common: "Lamarck's mangrove (hybrid)", family: 'Rhizophoraceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Bruguiera gymnorhiza', common: 'Large-leafed orange mangrove', family: 'Rhizophoraceae', local: 'Tumu / Putut',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Bruguiera cylindrica', common: 'Small-leafed orange mangrove', family: 'Rhizophoraceae', local: 'Bakau putih',
    regions: ['south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Bruguiera parviflora', common: 'Small-flowered orange mangrove', family: 'Rhizophoraceae', local: 'Lenggadai',
    regions: ['south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Bruguiera sexangula', common: 'Upriver orange mangrove', family: 'Rhizophoraceae', local: 'Busing / Tumu mata buaya',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Bruguiera hainesii', common: "Haines's orange mangrove", family: 'Rhizophoraceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Ceriops tagal', common: 'Yellow mangrove', family: 'Rhizophoraceae', local: 'Tengar',
    regions: ['south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Ceriops decandra', common: 'Spurred mangrove', family: 'Rhizophoraceae', local: 'Goran',
    regions: ['sundarbans','south_asia','southeast_asia','indo_pacific'] },
  { scientific: 'Ceriops zippeliana', common: "Zippel's spurred mangrove", family: 'Rhizophoraceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Kandelia candel', common: 'Kandelia', family: 'Rhizophoraceae', local: 'Pisang-pisang',
    regions: ['sundarbans','south_asia','southeast_asia','indo_pacific'] },
  { scientific: 'Kandelia obovata', common: 'East-Asian kandelia', family: 'Rhizophoraceae',
    regions: ['east_asia','indo_pacific'] },

  // ---- Acanthaceae (Avicennioideae) ----
  { scientific: 'Avicennia marina', common: 'Grey mangrove', family: 'Acanthaceae', local: 'Api-api putih',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Avicennia alba', common: 'White mangrove', family: 'Acanthaceae', local: 'Api-api',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Avicennia officinalis', common: 'Indian mangrove', family: 'Acanthaceae', local: 'Api-api ludat',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Avicennia rumphiana', common: 'Rumph mangrove', family: 'Acanthaceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },

  // ---- Lythraceae (Sonneratioideae) ----
  { scientific: 'Sonneratia alba', common: 'Mangrove apple', family: 'Lythraceae', local: 'Perepat / Pedada',
    regions: ['south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Sonneratia caseolaris', common: 'Crabapple mangrove', family: 'Lythraceae', local: 'Berembang',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Sonneratia apetala', common: 'Keora', family: 'Lythraceae', local: 'Keora',
    regions: ['sundarbans','south_asia','indo_pacific'] },
  { scientific: 'Sonneratia griffithii', common: "Griffith's mangrove apple", family: 'Lythraceae',
    regions: ['south_asia','southeast_asia','indo_pacific'] },
  { scientific: 'Sonneratia ovata', common: 'Ovate-leaved mangrove apple', family: 'Lythraceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Sonneratia lanceolata', common: 'Lanceolate mangrove apple', family: 'Lythraceae',
    regions: ['southeast_asia','indo_pacific'] },
  { scientific: 'Pemphis acidula', common: 'Bantigi / Digging stick tree', family: 'Lythraceae', local: 'Setigi',
    regions: ['southeast_asia','philippines','indo_pacific'] },

  // ---- Meliaceae ----
  { scientific: 'Xylocarpus granatum', common: 'Cannonball mangrove', family: 'Meliaceae', local: 'Nyirih bunga',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Xylocarpus moluccensis', common: 'Cedar mangrove', family: 'Meliaceae', local: 'Nyirih batu',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Xylocarpus rumphii', common: 'Rumph cedar mangrove', family: 'Meliaceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },

  // ---- Malvaceae ----
  { scientific: 'Heritiera fomes', common: 'Sundri', family: 'Malvaceae', local: 'Sundri',
    regions: ['sundarbans','south_asia'] },
  { scientific: 'Heritiera littoralis', common: 'Looking-glass mangrove', family: 'Malvaceae', local: 'Dungun',
    regions: ['south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Heritiera globosa', common: 'Borneo sundri', family: 'Malvaceae',
    regions: ['southeast_asia'] },
  { scientific: 'Camptostemon schultzii', common: "Schultz's kapok mangrove", family: 'Malvaceae',
    regions: ['southeast_asia','indo_pacific'] },
  { scientific: 'Camptostemon philippinensis', common: 'Philippine kapok mangrove', family: 'Malvaceae', local: 'Gapas-gapas',
    regions: ['philippines'] },
  { scientific: 'Brownlowia tersa', common: 'Brownlowia', family: 'Malvaceae',
    regions: ['sundarbans','south_asia','southeast_asia'] },

  // ---- Euphorbiaceae ----
  { scientific: 'Excoecaria agallocha', common: 'Milky / Blinding mangrove', family: 'Euphorbiaceae', local: 'Buta-buta',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Excoecaria indica', common: 'Indian blinding tree', family: 'Euphorbiaceae',
    regions: ['south_asia','southeast_asia'] },

  // ---- Combretaceae ----
  { scientific: 'Lumnitzera racemosa', common: 'White-flowered black mangrove', family: 'Combretaceae', local: 'Teruntum putih',
    regions: ['south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Lumnitzera littorea', common: 'Red-flowered black mangrove', family: 'Combretaceae', local: 'Teruntum merah',
    regions: ['southeast_asia','philippines','indo_pacific'] },
  { scientific: 'Lumnitzera x rosea', common: 'Pink-flowered black mangrove (hybrid)', family: 'Combretaceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },

  // ---- Primulaceae (Myrsinoideae) ----
  { scientific: 'Aegiceras corniculatum', common: 'River mangrove', family: 'Primulaceae', local: 'Kacang-kacang / Teruntum',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Aegiceras floridum', common: 'Black mangrove', family: 'Primulaceae',
    regions: ['southeast_asia','philippines','indo_pacific'] },

  // ---- Plumbaginaceae ----
  { scientific: 'Aegialitis rotundifolia', common: 'Round-leaved sea-club', family: 'Plumbaginaceae',
    regions: ['sundarbans','south_asia','southeast_asia'] },

  // ---- Rubiaceae ----
  { scientific: 'Scyphiphora hydrophyllacea', common: 'Nilad', family: 'Rubiaceae', local: 'Chiengkareng',
    regions: ['south_asia','southeast_asia','philippines','indo_pacific'] },

  // ---- Fabaceae ----
  { scientific: 'Cynometra iripa', common: 'Wrinkle pod mangrove', family: 'Fabaceae',
    regions: ['south_asia','southeast_asia','indo_pacific'] },
  { scientific: 'Cynometra ramiflora', common: 'Wrinkle-pod mangrove', family: 'Fabaceae', local: 'Katong-katong',
    regions: ['south_asia','southeast_asia','philippines','indo_pacific'] },

  // ---- Bignoniaceae ----
  { scientific: 'Dolichandrone spathacea', common: 'Mangrove trumpet tree', family: 'Bignoniaceae', local: 'Tui',
    regions: ['south_asia','southeast_asia','philippines','indo_pacific'] },

  // ---- Arecaceae ----
  { scientific: 'Nypa fruticans', common: 'Nipa / Mangrove palm', family: 'Arecaceae', local: 'Nipah',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Phoenix paludosa', common: 'Mangrove date palm', family: 'Arecaceae', local: 'Hintal',
    regions: ['sundarbans','south_asia','southeast_asia'] },

  // ---- Pteridaceae (mangrove ferns) ----
  { scientific: 'Acrostichum aureum', common: 'Golden leather fern', family: 'Pteridaceae', local: 'Piai raya',
    regions: ['sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
  { scientific: 'Acrostichum speciosum', common: 'Mangrove fern', family: 'Pteridaceae', local: 'Piai lasa',
    regions: ['south_asia','southeast_asia','philippines','indo_pacific'] },

  // ---- Fabaceae (associate) ----
  { scientific: 'Intsia bijuga', common: 'Borneo teak / Merbau ipil', family: 'Fabaceae', local: 'Merbau',
    regions: ['southeast_asia','philippines','indo_pacific'] },

  // Catch-all (always shown)
  { scientific: 'Other / Unidentified', common: '', family: '', regions: ['all','sundarbans','south_asia','southeast_asia','philippines','east_asia','indo_pacific'] },
];

export const SPECIES_BY_NAME = ASIAN_MANGROVES.reduce((acc, m) => {
  acc[m.scientific] = m;
  return acc;
}, {});

/** Return species list filtered by region code; 'all' or falsy = full list. */
export function speciesForRegion(region) {
  if (!region || region === 'all') return ASIAN_MANGROVES;
  return ASIAN_MANGROVES.filter((m) => (m.regions || []).includes(region));
}
